<template>
  <div class="login-page">
    <div class="login-container">
      <!-- Logo Section -->
      <div class="logo-section-white">
        <img src="/invoxis-logo.png" alt="Invoxis" class="company-logo" />
      </div>
      <div class="logo-section-gradient">
        <p class="app-subtitle">Professional Property Management System</p>
      </div>

      <!-- Login Form -->
      <div class="login-form-container">
        <form @submit.prevent="handleLogin" class="login-form">
          <h2 class="form-title">Welcome Back</h2>
          <p class="form-subtitle">Sign in to your account</p>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            <span class="error-icon">⚠️</span>
            {{ errorMessage }}
          </div>

          <!-- Email Field -->
          <div class="form-group">
            <label for="email" class="form-label">Email</label>
            <div class="input-container">
              <span class="input-icon">📧</span>
              <input
                type="email"
                v-model="username"
                id="email"
                class="form-input"
                placeholder="Enter your email"
                required
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="input-container">
              <span class="input-icon">🔒</span>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                id="password"
                class="form-input"
                placeholder="Enter your password"
                required
                :disabled="isLoading"
              />
              <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                <span v-if="showPassword">👁️</span>
                <span v-else>👁️‍🗨️</span>
              </button>
            </div>
          </div>

          <!-- Submit Button -->
          <button type="submit" class="submit-btn" :disabled="isLoading">
            <span v-if="isLoading" class="loading-spinner"></span>
            <span v-if="isLoading">Signing In...</span>
            <span v-else>Sign In</span>
          </button>

          <!-- Register Link -->
          <div class="register-link">
            <span>Don't have an account?</span>
            <router-link to="/register" class="link">Create Account</router-link>
          </div>

        </form>
      </div>
    </div>

    <!-- Pending Validation Modal -->
    <div v-if="showPendingModal" class="modal-overlay" @click.self="showPendingModal = false">
      <div class="modal-container">
        <div class="modal-icon">⏳</div>
        <h3 class="modal-title">Account Pending Approval</h3>
        <p class="modal-message">
          Your account is currently awaiting approval from an administrator.
          You will receive an email notification once your account has been activated.
        </p>
        <p class="modal-submessage">
          Thank you for your patience.
        </p>
        <button @click="showPendingModal = false" class="modal-btn">
          OK, I Understand
        </button>
      </div>
    </div>

    <!-- Background Elements -->
    <div class="background-elements">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'UserLogin',
  data() {
    return {
      username: '',
      password: '',
      errorMessage: '',
      isLoading: false,
      showPassword: false,
      showPendingModal: false
    };
  },
  
  methods: {
    async handleLogin() {
      this.errorMessage = '';
      this.isLoading = true;

      const loginData = {
        email: this.username,  // Backend expects email field
        password: this.password,
      };

      try {
        const response = await fetch(`${SETTINGS.API.baseUrl}/api/users/login/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginData),
        });

        const data = await response.json();

        if (!response.ok) {
          // Check for pending validation error
          if (data.errors && data.errors.non_field_errors) {
            const errorMsg = data.errors.non_field_errors[0] || '';
            if (errorMsg.includes('attente') || errorMsg.includes('pending') || errorMsg.includes('validation')) {
              this.showPendingModal = true;
              return;
            }
          }

          if (response.status === 401) {
            throw new Error('Invalid credentials. Please check your email and password.');
          }
          throw new Error(data.message || data.detail || `Login failed: ${response.statusText}`);
        }

        if (data.status === 'success' || data.token) {
          // Save tokens to localStorage
          localStorage.setItem('token', data.token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }

          // Save user info
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }

          // Redirect to hub
          this.$router.push('/');
        } else {
          throw new Error(data.message || 'Login failed. Please try again.');
        }
      } catch (error) {
        console.error('Login error:', error);
        this.errorMessage = error.message;

        // Clear password on error
        this.password = '';
      } finally {
        this.isLoading = false;
      }
    }
  },
  
  mounted() {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.bg-circle-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.bg-circle-2 {
  width: 200px;
  height: 200px;
  top: 50%;
  right: -100px;
  animation-delay: 2s;
}

.bg-circle-3 {
  width: 150px;
  height: 150px;
  bottom: -75px;
  left: 30%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.login-container {
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 2;
  animation: slideUp 0.8s ease-out;
}

@keyframes slideUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-section-white {
  background: white;
  padding: 15px 30px 10px 30px;
  text-align: center;
}

.logo-section-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 30px;
  text-align: center;
  color: white;
}

.company-logo {
  max-width: 220px;
  height: auto;
  margin: 0 auto;
  display: block;
}

.logo {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.logo-icon {
  font-size: 32px;
}

.app-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.app-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.login-form-container {
  padding: 40px 30px;
}

.form-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 8px 0;
  text-align: center;
}

.form-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 30px 0;
  text-align: center;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.error-icon {
  font-size: 16px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  z-index: 2;
  font-size: 16px;
  opacity: 0.7;
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 50px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-password {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.toggle-password:hover {
  opacity: 1;
}

.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 25px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.register-link {
  text-align: center;
  font-size: 14px;
  color: #64748b;
}

.register-link .link {
  color: #667eea;
  font-weight: 600;
  text-decoration: none;
  margin-left: 6px;
  transition: color 0.3s ease;
}

.register-link .link:hover {
  color: #764ba2;
  text-decoration: underline;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.demo-credentials {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.demo-title {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.demo-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 13px;
}

.demo-info:last-child {
  margin-bottom: 0;
}

.demo-label {
  color: #64748b;
  font-weight: 500;
}

.demo-value {
  color: #1a202c;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  background: #e2e8f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

/* Pending Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 15px 0;
}

.modal-message {
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 10px 0;
}

.modal-submessage {
  font-size: 14px;
  color: #94a3b8;
  font-style: italic;
  margin: 0 0 25px 0;
}

.modal-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 30px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 480px) {
  .login-page {
    padding: 15px;
  }
  
  .login-container {
    max-width: 100%;
  }
  
  .logo-section {
    padding: 30px 20px;
  }
  
  .login-form-container {
    padding: 30px 20px;
  }
  
  .app-title {
    font-size: 24px;
  }
  
  .form-title {
    font-size: 20px;
  }
}
</style>